// Kiosk ticket list — full-screen searchable card grid for the locked
// weighbridge. A "New Ticket" card always leads the grid (visually distinct);
// every ticket renders as a touch card with a colour-coded status badge.
// Search filters customer name / ticket number / vehicle reg in real time.
// Auto-refreshes every 5s via the shared useWbTickets polling option.

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWbTickets } from '../hooks/useWbTickets';
import type { TicketStatus, TicketSummaryDto } from '../hooks/useWbTickets';
import { useAuthStore } from '../../../store/authStore';
import { useWbLiveFeed } from '../../../hooks/wb/useWbLiveFeed';
import { queryKeys } from '../../../api/queryKeys';
import { statusPillClass } from './statusPillClass';
import styles from './kiosk.module.css';

const MAX_VISIBLE = 60;

// Statuses surfaced at the kiosk. Drafts + open are the working set; recently
// completed tickets stay visible so the operator can re-print. Voided tickets
// are included so a mistaken void is still discoverable via search.
const KIOSK_STATUSES: TicketStatus[] = ['draft', 'open', 'completed', 'voided'];

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString([], {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function matchesSearch(t: TicketSummaryDto, q: string): boolean {
  if (q === '') return true;
  const haystack = [
    t.customer?.name ?? '',
    t.ticket_number ?? '',
    t.vehicle_reg_snapshot ?? '',
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export interface KioskTicketListProps {
  readonly weighbridgeEncId: string;
  readonly onNew: () => void;
  readonly onOpen: (encId: string) => void;
}

export function KioskTicketList({ weighbridgeEncId, onNew, onOpen }: KioskTicketListProps) {
  const { data, isLoading, isError } = useWbTickets(
    { status: KIOSK_STATUSES, weighbridge_enc_id: weighbridgeEncId, per_page: MAX_VISIBLE },
    { enablePolling: true },
  );

  const qc = useQueryClient();
  // ticket_weight_updated carries a rotating enc_id, so this is a coarse
  // invalidateQueries (not a targeted cache patch — matching that ciphertext
  // against a cached row is the exact enc_id-rotation bug class this codebase
  // has hit before); it also doesn't unblock relaxing the 5s poll below,
  // since other mutations (lock/status changes) still aren't signalled —
  // it just keeps the weight column fresh for viewers who aren't the one
  // capturing, without waiting for the next poll tick. This does open one
  // extra SSE connection while the list screen is showing, on top of Task
  // 1's per-tab consolidation — accepted for this task.
  useWbLiveFeed({
    onTicketWeightUpdated: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.tickets() });
    },
  });

  // Current staff id — a lock badge is only shown for tickets locked by SOMEONE
  // ELSE (you already hold your own lock, so don't flag it).
  const currentStaffId = useAuthStore((s) => s.user?.id ?? null);
  const [search, setSearch] = useState('');

  const tickets = data?.tickets ?? [];
  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () => tickets.filter((t) => matchesSearch(t, q)),
    [tickets, q],
  );

  return (
    <div className={styles.gridScreen}>
      <input
        type="search"
        className={styles.searchBar}
        placeholder="Search tickets — customer, ticket no, vehicle reg…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search tickets"
        data-testid="kiosk-ticket-search"
      />

      {isError ? <div className={styles.error}>Could not load tickets.</div> : null}

      <div className={styles.ticketGrid} data-testid="kiosk-ticket-grid">
        {/* New Ticket card — always first, visually distinct. */}
        <button
          type="button"
          className={`${styles.ticketGridCard} ${styles.newTicketCard}`}
          onClick={onNew}
          data-testid="kiosk-new-ticket-card"
        >
          <span className={styles.newTicketPlus}>+</span>
          <span className={styles.newTicketLabel}>New Ticket</span>
        </button>

        {filtered.map((t) => {
          const lineCount = t.line_count ?? (t.line_preview?.length ?? 0);
          const lockedByOther =
            t.lock != null && t.lock.locked_by !== currentStaffId ? t.lock : null;
          return (
            <button
              key={t.enc_id}
              type="button"
              className={`${styles.ticketGridCard}${t.direction === 'outwards' ? ` ${styles.ticketGridCardOutbound}` : ''}`}
              onClick={() => onOpen(t.enc_id)}
              data-testid={`kiosk-ticket-${t.enc_id}`}
            >
              {/* Lock badge — only when held by ANOTHER staff member. */}
              {lockedByOther ? (
                <span
                  className={styles.ticketLockBadge}
                  data-testid={`kiosk-ticket-lock-${t.enc_id}`}
                >
                  🔒 {lockedByOther.locked_by_name}
                </span>
              ) : null}

              {/* Print badge */}
              {t.printed_at ? (
                <span
                  className={styles.printedBadge}
                  title={`Printed ${fmtDate(t.printed_at)}`}
                >
                  🖨 Printed
                </span>
              ) : null}

              <div className={styles.ticketCardHead}>
                <span className={styles.ticketNo}>{t.ticket_number ?? 'Draft'}</span>
                <span className={statusPillClass(t.status)}>{t.status}</span>
              </div>
              <span className={styles.ticketCardCustomer}>{t.customer?.name ?? '—'}</span>
              <div className={styles.ticketCardMeta}>
                <span
                  className={
                    t.direction === 'inwards' ? styles.badgeInbound : styles.badgeOutbound
                  }
                >
                  {t.direction === 'inwards' ? 'Inbound' : 'Outbound'}
                </span>
                {t.vehicle_reg_snapshot ? <span>{t.vehicle_reg_snapshot}</span> : null}
                {t.net_weight_kg != null ? (
                  <span>{t.net_weight_kg.toLocaleString()} kg</span>
                ) : null}
              </div>

              {lineCount > 0 ? (
                <span className={styles.lineCountBadge}>
                  {lineCount} {lineCount === 1 ? 'line' : 'lines'}
                </span>
              ) : null}

              <span className={styles.ticketCardDate}>{fmtDate(t.created_at)}</span>
            </button>
          );
        })}
      </div>

      {isLoading ? <p className={styles.muted}>Loading…</p> : null}
      {!isLoading && !isError && tickets.length === 0 ? (
        <p className={styles.muted}>No tickets yet. Tap “New Ticket” to start.</p>
      ) : null}
      {!isLoading && !isError && tickets.length > 0 && filtered.length === 0 ? (
        <p className={styles.muted}>No tickets match “{search}”.</p>
      ) : null}
    </div>
  );
}
